import React, { useEffect } from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, Button, List, ListItem, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { DataObject, Language, AutoAwesome, CheckCircleOutline } from '@mui/icons-material';

const services = [
  {
    icon: <DataObject sx={{ fontSize: 32 }} />,
    title: 'Python Automation',
    description: 'We design and implement automation systems that eliminate repetitive tasks, streamline operations, and improve efficiency. From data scraping and reporting automation to backend workflows and integrations, we build scalable Python solutions tailored to your needs.',
    points: [
      'Workflow automation',
      'Data scraping & processing',
      'API integrations',
      'Reporting & analytics automation',
    ],
    bgGlow: 'rgba(99, 102, 241, 0.08)'
  },
  {
    icon: <Language sx={{ fontSize: 32 }} />,
    title: 'Web Development',
    description: 'We build modern, responsive, and high-performance web applications using scalable architectures and clean design principles. Our solutions focus on performance, usability, and long-term maintainability.',
    points: [
      'Full-stack web applications',
      'SaaS platforms',
      'Custom dashboards',
      'Performance optimization',
    ],
    bgGlow: 'rgba(6, 182, 212, 0.08)'
  },
  {
    icon: <AutoAwesome sx={{ fontSize: 32 }} />,
    title: 'Machine Learning Solutions',
    description: 'We develop intelligent systems that turn data into actionable insights. From predictive models to automation powered by AI, we help businesses make data-driven decisions with confidence.',
    points: [
      'Predictive modeling',
      'NLP & text analysis',
      'Data-driven automation',
      'Model deployment & integration',
    ],
    bgGlow: 'rgba(236, 72, 153, 0.08)'
  },
];

const Services = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;

  useEffect(() => {
    document.title = "Our Services | CoreTechTalks";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Explore our technical services including Python automation, web development, and custom machine learning solutions built for scale at CoreTechTalks.");
    }
  }, []);

  return (
    <Box sx={{ flexGrow: 1, py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>
      {/* Ambient background glows */}
      <Box 
        className="glow-orb" 
        sx={{ 
          width: '500px', 
          height: '500px', 
          top: '-10%', 
          left: '10%', 
          background: 'rgba(99, 102, 241, 0.06)' 
        }} 
      />
      <Box 
        className="glow-orb" 
        sx={{ 
          width: '400px', 
          height: '400px', 
          bottom: '10%', 
          right: '5%', 
          background: 'rgba(6, 182, 212, 0.06)' 
        }} 
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ fontWeight: 800, fontSize: { xs: '38px', md: '52px' }, mb: 2, lineHeight: 1.1 }}
          >
            Technology Solutions <span className="gradient-text">Built for Scale</span>
          </Typography>
          <Typography 
            variant="h2"
            sx={{ 
              color: 'text.secondary', 
              fontSize: { xs: '18px', md: '20px' }, 
              fontWeight: 500,
              maxWidth: '700px', 
              mx: 'auto', 
              mb: 5,
              lineHeight: 1.6
            }}
          >
            We help businesses automate workflows, build high-performance web applications, and leverage machine learning for smarter decision-making.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            component={Link}
            to="/contact"
            sx={{ px: 4, py: 1.6 }}
          >
            Request a Consultation
          </Button>
        </Box>

        {/* Services Section */}
        <Grid container spacing={4} justifyContent="center">
          {services.map((service, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 3 }}>
                    {/* Icon container */}
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        width: '72px', 
                        height: '72px', 
                        borderRadius: '16px', 
                        background: service.bgGlow,
                        color: mode === 'light' ? 'primary.main' : 'primary.light',
                        mb: 3,
                        border: `1px solid ${mode === 'light' ? 'rgba(79, 70, 229, 0.1)' : 'rgba(129, 140, 248, 0.15)'}`
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h4" component="h2" sx={{ fontSize: '24px', fontWeight: 800, mb: 2 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, minHeight: { md: '100px' } }}>
                      {service.description}
                    </Typography>
                  </Box>
                  
                  <List dense sx={{ mt: 'auto', pt: 2, borderTop: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}` }}>
                    {service.points.map((point, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: '28px', color: mode === 'light' ? '#06B6D4' : '#22D3EE' }}>
                          <CheckCircleOutline sx={{ fontSize: '18px' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={point} 
                          primaryTypographyProps={{ 
                            fontSize: '14px', 
                            fontWeight: 600,
                            color: 'text.primary' 
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Services;
